import { useEffect, useRef, type RefObject } from 'react';
import translations from '../../../../../translations';
import ButtonDefault from '../../../../buttons/ButtonDefault';
import ConfirmModal from '../../../../generic/modal/ConfirmModal';
import { SUMMARY_CONTEXT } from '../../../constants';
import { defaultPageConditionConfig } from '../../../templates';
import { usePageEditor } from '../../PageEditorProvider';
import ConfigSection from '../composer/config-panel/ConfigSection';
import PageConditionItemList from './common/PageConditionItemList';
import Portal from './common/Portal';
import { usePageCondition } from './PageConditionsProvider';
import PageConditionConfig from './pageConditionConfig/PageConditionConfig';

const PageConditions = ({
    parentContainerRef,
}: {
    parentContainerRef: RefObject<HTMLDivElement | null>;
}) => {
    const {
        toggleUnsavedChanges,
        setModifiedPageConditions,
        state: pageBuilderState,
    } = usePageEditor();
    const { tab, page } = pageBuilderState;
    const { pageConditions = [] } = page;
    const { state: pageConditionState, updateCondition, updateConditionIndex } = usePageCondition();
    const { conditionIndex, confirmation: confirmProps } = pageConditionState;

    const scrollValue = useRef<number | undefined>(0);
    const footerPortalId = `sidebar-right-footer-portal-${tab?.key}`;

    const captureScrollPosition = () => {
        scrollValue.current = parentContainerRef.current?.scrollTop;
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        // Load the selected condition into the context.
        if (conditionIndex !== null && conditionIndex >= 0 && pageConditions) {
            updateCondition(pageConditions[conditionIndex]);
        }

        // A condition was canceled so any unsaved changes (rules/operations) become irrelevant.
        if (conditionIndex === null) {
            if (scrollValue.current !== undefined && parentContainerRef.current !== null) {
                parentContainerRef.current.scrollTop = scrollValue.current;
            }
            setModifiedPageConditions(false);
            toggleUnsavedChanges(false);
        }
    }, [conditionIndex]);

    const onAddNewCondition = () => {
        captureScrollPosition();
        updateConditionIndex(-1);
        updateCondition(defaultPageConditionConfig);
        setModifiedPageConditions(true);
    };

    return (
        <>
            <h2 className="sidebar-main-heading">
                {translations.PAGE_BUILDER_page_conditions_heading}
            </h2>
            <ConfigSection dataTestId="page-conditions">
                {conditionIndex === null ? (
                    <>
                        <PageConditionItemList
                            listContext={SUMMARY_CONTEXT['CONDITIONS']}
                            onBeforeEditItem={captureScrollPosition}
                        />
                        <Portal wrapperId={footerPortalId}>
                            <footer className="sidebar-section-footer">
                                <ButtonDefault
                                    className="align-left"
                                    onClick={onAddNewCondition}
                                    title="Add new page condition"
                                >
                                    Add condition
                                </ButtonDefault>
                            </footer>
                        </Portal>
                    </>
                ) : (
                    <PageConditionConfig />
                )}
            </ConfigSection>
            {confirmProps && <ConfirmModal {...confirmProps} />}
        </>
    );
};

export default PageConditions;
